import {
    UserSegmentationTeamTypes,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
    USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL
} from "../../models/user-segmentation";

import { templateChoices } from "./template-definitions";

import type { TemplateChoice, TemplateOption } from "./template-definitions";

/**
 * Gets the team type template options based on team type for Transparent Template for all Iteration 2 Experiment
 * @param teamType The team type
 * @param formatMessage function to format the message for the current locale
 * @returns TemplateOption[]
 */

function getItSupportTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.itSupportKanban,
                title: formatMessage({
                    id: "TransparentTemplateV2.itSupport.kanban.title",
                    defaultMessage: "Kanban"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.itSupport.kanban.subtext",
                    defaultMessage:
                        "Visualize work and maximize efficiency with a kanban board"
                })
            }
        },
        {
            option: {
                ...templateChoices.itSupportScrum,
                title: formatMessage({
                    id: "TransparentTemplateV2.scrum.title",
                    defaultMessage: "Scrum"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.scrum.subtext",
                    defaultMessage:
                        "Plan, prioritize, and schedule sprints using scrum framework"
                })
            }
        },
        {
            option: {
                ...templateChoices.itSupportProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.itSupport.projectManagement.title",
                    defaultMessage: "Project management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.itSupport.projectManagement.subtext",
                    defaultMessage:
                        "Designed to help business teams manage work with list and calendar views"
                })
            }
        }
    ];
}

/**
 * Gets the team type template options based on team type for Transparent Template for all Iteration 2 Experiment
 * @param teamType The team type
 * @param formatMessage function to format the message for the current locale
 * @returns TemplateOption[]
 */
function getProductManagementTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.productManagementScrum,
                title: formatMessage({
                    id: "TransparentTemplateV2.scrum.title",
                    defaultMessage: "Scrum"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.scrum.subtext",
                    defaultMessage:
                        "Plan, prioritize, and schedule sprints using scrum framework"
                })
            }
        },
        {
            option: {
                ...templateChoices.productManagementProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.productManagement.projectManagement.title",
                    defaultMessage: "Project management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.productManagement.projectManagement.subtext",
                    defaultMessage:
                        "Manage and track agile work plus integrate developer tools like GitHub"
                })
            }
        },
        {
            option: {
                ...templateChoices.productManagementBusinessCollaboration,
                title: formatMessage({
                    id: "TransparentTemplateV2.productManagement.businessCollaboration.title",
                    defaultMessage: "Business collaboration"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.productManagement.businessCollaboration.subtext",
                    defaultMessage:
                        "Designed to help business teams manage work with list and calendar views"
                })
            }
        }
    ];
}

function getHumanResourcesTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.humanResourcesProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.humanResources.projectManagement.title",
                    defaultMessage: "Project management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.humanResources.projectManagement.subtext",
                    defaultMessage:
                        "Manage, track, and assign tasks to streamline project workflows"
                })
            }
        },
        {
            option: {
                ...templateChoices.humanResourcesNewEmployeeOnboarding,
                title: formatMessage({
                    id: "TransparentTemplateV2.humanResources.newEmployeeOnboarding.title",
                    defaultMessage: "New employee onboarding"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.humanResources.newEmployeeOnboarding.subtext",
                    defaultMessage:
                        "Manage and standardize the onboarding process for all new hires"
                })
            }
        },
        {
            option: {
                ...templateChoices.humanResourcesRecruitment,
                title: formatMessage({
                    id: "TransparentTemplateV2.humanResources.recruitment.title",
                    defaultMessage: "Recruitment"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.humanResources.recruitment.subtext",
                    defaultMessage:
                        "Track potential candidates from application to offer"
                })
            }
        }
    ];
}

function getCustomerServiceTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.customerServiceKanban,
                title: formatMessage({
                    id: "TransparentTemplateV2.customerService.kanban.title",
                    defaultMessage: "Kanban"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.customerService.kanban.subtext",
                    defaultMessage:
                        "Visualize work and maximize efficiency with a kanban board"
                })
            }
        },
        {
            option: {
                ...templateChoices.customerServiceScrum,
                title: formatMessage({
                    id: "TransparentTemplateV2.scrum.title",
                    defaultMessage: "Scrum"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.scrum.subtext",
                    defaultMessage:
                        "Plan, prioritize, and schedule sprints using scrum framework"
                })
            }
        },
        {
            option: {
                ...templateChoices.customerServiceProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.customerService.projectManagement.title",
                    defaultMessage: "Project management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.customerService.projectManagement.subtext",
                    defaultMessage:
                        "Manage, track, and assign tasks to streamline project workflows"
                })
            }
        }
    ];
}

function getLegalTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.legalProcessControl,
                title: formatMessage({
                    id: "TransparentTemplateV2.legal.processControl.title",
                    defaultMessage: "Process control"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.legal.processControl.subtext",
                    defaultMessage:
                        "Create, monitor, and iterate on processes in one place"
                })
            }
        },
        {
            option: {
                ...templateChoices.legalProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.legal.projectManagement.title",
                    defaultMessage: "Project management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.legal.projectManagement.subtext",
                    defaultMessage:
                        "Manage, track, and assign tasks to streamline project workflows"
                })
            }
        },
        {
            option: {
                ...templateChoices.legalIpInfringement,
                title: formatMessage({
                    id: "TransparentTemplateV2.legal.ipInfringement.title",
                    defaultMessage: "IP Infringement"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.legal.ipInfringement.subtext",
                    defaultMessage:
                        "Identify and track IP infringement claims to resolve them."
                })
            }
        }
    ];
}

function getFinanceTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.financeProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.finance.projectManagement.title",
                    defaultMessage: "Project Management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.finance.projectManagement.subtext",
                    defaultMessage:
                        "Manage, track, and assign tasks to streamline project workflows"
                })
            }
        },
        {
            option: {
                ...templateChoices.financeProcurement,
                title: formatMessage({
                    id: "TransparentTemplateV2.finance.procurement.title",
                    defaultMessage: "Procurement"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.finance.procurement.subtext",
                    defaultMessage:
                        "Standardize purchase processes on your team from request to receipt"
                })
            }
        },
        {
            option: {
                ...templateChoices.financeBudgetCreation,
                title: formatMessage({
                    id: "TransparentTemplateV2.finance.budgetCreation.title",
                    defaultMessage: "Budget Creation"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.finance.budgetCreation.subtext",
                    defaultMessage:
                        "Manage, approve, or reject budget proposals all in one place"
                })
            }
        }
    ];
}

function getDataScienceTemplateOptions(
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    return [
        {
            option: {
                ...templateChoices.dataScienceKanban,
                title: formatMessage({
                    id: "TransparentTemplateV2.dataScience.kanban.title",
                    defaultMessage: "Kanban"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.dataScience.kanban.subtext",
                    defaultMessage:
                        "Manage data science projects efficiently, visualize tasks, and see team workload"
                })
            }
        },
        {
            option: {
                ...templateChoices.dataScienceScrum,
                title: formatMessage({
                    id: "TransparentTemplateV2.scrum.title",
                    defaultMessage: "Scrum"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.scrum.subtext",
                    defaultMessage:
                        "Plan, prioritize, and schedule sprints using scrum framework"
                })
            }
        },
        {
            option: {
                ...templateChoices.dataScienceProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.dataScience.projectManagement.title",
                    defaultMessage: "Project Management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.dataScience.projectManagement.subtext",
                    defaultMessage:
                        "Manage, track, and assign tasks to streamline project workflows"
                })
            }
        }
    ];
}

// eslint-disable-next-line complexity
export function getTeamTypeTemplateOptions(
    teamType: UserSegmentationTeamTypes,
    formatMessage: (parameters: {
        id: string;
        defaultMessage: string;
    }) => string
): TemplateOption[] {
    const defaultTemplateOptions = [
        {
            option: {
                ...templateChoices.softwareDevelopmentKanban,
                title: formatMessage({
                    id: "TransparentTemplateV2.softwareDevelopment.kanban.title",
                    defaultMessage: "Kanban"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.softwareDevelopment.kanban.subtext",
                    defaultMessage:
                        "Visualize work and maximize efficiency with a kanban board"
                })
            } as TemplateChoice
        },
        {
            option: {
                ...templateChoices.softwareDevelopmentScrum,
                title: formatMessage({
                    id: "TransparentTemplateV2.scrum.title",
                    defaultMessage: "Scrum"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.scrum.subtext",
                    defaultMessage:
                        "Plan, prioritize, and schedule sprints using scrum framework"
                })
            } as TemplateChoice
        },
        {
            option: {
                ...templateChoices.softwareDevelopmentProjectManagement,
                title: formatMessage({
                    id: "TransparentTemplateV2.softwareDevelopment.projectManagement.title",
                    defaultMessage: "Project management"
                }),
                subtext: formatMessage({
                    id: "TransparentTemplateV2.softwareDevelopment.projectManagement.subtext",
                    defaultMessage:
                        "Manage and track agile work plus integrate developer tools like GitHub"
                })
            } as TemplateChoice
        }
    ];
    switch (teamType) {
        case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
            return [
                {
                    option: {
                        ...templateChoices.softwareDevelopmentKanban,
                        title: formatMessage({
                            id: "TransparentTemplateV2.softwareDevelopment.kanban.title",
                            defaultMessage: "Kanban"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.softwareDevelopment.kanban.subtext",
                            defaultMessage:
                                "Visualize work and maximize efficiency with a kanban board"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.softwareDevelopmentScrum,
                        title: formatMessage({
                            id: "TransparentTemplateV2.scrum.title",
                            defaultMessage: "Scrum"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.scrum.subtext",
                            defaultMessage:
                                "Plan, prioritize, and schedule sprints using scrum framework"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.softwareDevelopmentProjectManagement,
                        title: formatMessage({
                            id: "TransparentTemplateV2.softwareDevelopment.projectManagement.title",
                            defaultMessage: "Project management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.softwareDevelopment.projectManagement.subtext",
                            defaultMessage:
                                "Manage and track agile work plus integrate developer tools like GitHub"
                        })
                    }
                }
            ];
        case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
            return getProductManagementTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
            return [
                {
                    option: {
                        ...templateChoices.campaignManagement,
                        title: formatMessage({
                            id: "TransparentTemplate.marketing.campaignManagement.title",
                            defaultMessage: "Campaign management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplate.marketing.campaignManagement.subtext",
                            defaultMessage:
                                "Work towards campaign launches, tracking deadlines and tasks along the way"
                        })
                    },
                    metadata: {
                        jtbd: "1,5"
                    }
                },
                {
                    option: {
                        ...templateChoices.marketingProjectManagement,
                        title: formatMessage({
                            id: "TransparentTemplate.marketing.marketingProjectManagement.title",
                            defaultMessage: "Marketing project management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplate.marketing.marketingProjectManagement.subtext",
                            defaultMessage:
                                "Manage marketing projects efficiently, visualize tasks, and see team workload"
                        })
                    },
                    metadata: {
                        jtbd: "4,5"
                    }
                },
                {
                    option: {
                        ...templateChoices.contentManagement,
                        title: formatMessage({
                            id: "TransparentTemplate.marketing.contentManagement.title",
                            defaultMessage: "Content management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplate.marketing.contentManagement.subtext",
                            defaultMessage:
                                "Oversee your marketing content lifecycle from draft to delivery"
                        })
                    },
                    metadata: { jtbd: "2,6,5" }
                }
            ];
        case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
            return [
                {
                    option: {
                        ...templateChoices.designKanban,
                        title: formatMessage({
                            id: "TransparentTemplateV2.design.kanban.title",
                            defaultMessage: "Kanban"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.design.kanban.subtext",
                            defaultMessage:
                                "Manage design projects efficiently, visualize tasks, and see team workload"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.designWebDesignProcess,
                        title: formatMessage({
                            id: "TransparentTemplateV2.design.webDesignProcess.title",
                            defaultMessage: "Web design process"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.design.webDesignProcess.subtext",
                            defaultMessage:
                                "Build web pages plus keep designers and developers on the same page"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.designScrum,
                        title: formatMessage({
                            id: "TransparentTemplateV2.scrum.title",
                            defaultMessage: "Scrum"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.scrum.subtext",
                            defaultMessage:
                                "Plan, prioritize, and schedule sprints using scrum framework"
                        })
                    }
                }
            ];
        case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
            return [
                {
                    option: {
                        ...templateChoices.projectManagementKanban,
                        title: formatMessage({
                            id: "TransparentTemplateV2.projectManagement.kanban.title",
                            defaultMessage: "Kanban"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.projectManagement.kanban.subtext",
                            defaultMessage:
                                "Manage and track agile work plus integrate developer tools like GitHub"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.projectManagementProjectManagement,
                        title: formatMessage({
                            id: "TransparentTemplateV2.projectManagement.projectManagement.title",
                            defaultMessage: "Project management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.projectManagement.projectManagement.subtext",
                            defaultMessage:
                                "Designed to help business teams manage work with list and calendar views"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.projectManagementScrum,
                        title: formatMessage({
                            id: "TransparentTemplateV2.scrum.title",
                            defaultMessage: "Scrum"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.scrum.subtext",
                            defaultMessage:
                                "Plan, prioritize, and schedule sprints using scrum framework"
                        })
                    }
                }
            ];
        case USER_SEGMENTATION_TEAM_TYPE_OPERATIONS:
            return [
                {
                    option: {
                        ...templateChoices.processControl,
                        title: formatMessage({
                            id: "TransparentTemplate.operations.processControl.title",
                            defaultMessage: "Process control"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplate.operations.processControl.subtext",
                            defaultMessage:
                                "Create, monitor, and iterate on processes in one place"
                        })
                    },
                    metadata: {
                        jtbd: "3,2"
                    }
                },
                {
                    option: {
                        ...templateChoices.projectManagement,
                        title: formatMessage({
                            id: "TransparentTemplate.operations.projectManagement.title",
                            defaultMessage: "Project management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplate.operations.projectManagement.subtext",
                            defaultMessage:
                                "Designed to help business teams manage work with list and calendar views"
                        })
                    },
                    metadata: {
                        jtbd: "5,2"
                    }
                },
                {
                    option: {
                        ...templateChoices.procurement,
                        title: formatMessage({
                            id: "TransparentTemplate.operations.procurement.title",
                            defaultMessage: "Procurement"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplate.operations.procurement.subtext",
                            defaultMessage:
                                "Standardize purchase process on your team from request to receipt"
                        })
                    },
                    metadata: {
                        jtbd: "7,2"
                    }
                }
            ];

        case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
            return getItSupportTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_HR:
            return getHumanResourcesTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE:
            return getCustomerServiceTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_LEGAL:
            return getLegalTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_FINANCE:
            return getFinanceTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_SALES:
            return [
                {
                    option: {
                        ...templateChoices.salesProjectManagement,
                        title: formatMessage({
                            id: "TransparentTemplateV2.sales.projectManagement.title",
                            defaultMessage: "Project management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.sales.projectManagement.subtext",
                            defaultMessage:
                                "Manage, track, and assign tasks to streamline project workflows"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.salesSalesPipeline,
                        title: formatMessage({
                            id: "TransparentTemplateV2.sales.salesPipeline.title",
                            defaultMessage: "Sales pipeline"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.sales.salesPipeline.subtext",
                            defaultMessage:
                                "Manage and track your customers to help close deals with ease"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.salesLeadTracking,
                        title: formatMessage({
                            id: "TransparentTemplateV2.sales.leadTracking.title",
                            defaultMessage: "Lead tracking"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.sales.leadTracking.subtext",
                            defaultMessage:
                                "Track sales leads from opportunity through to conversion"
                        })
                    }
                }
            ];
        case USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE:
            return getDataScienceTemplateOptions(formatMessage);
        case USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL:
            return [
                {
                    option: {
                        ...templateChoices.otherScrum,
                        title: formatMessage({
                            id: "TransparentTemplateV2.scrum.title",
                            defaultMessage: "Scrum"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.scrum.subtext",
                            defaultMessage:
                                "Plan, prioritize, and schedule sprints using scrum framework"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.otherKanban,
                        title: formatMessage({
                            id: "TransparentTemplateV2.other.kanban.title",
                            defaultMessage: "Kanban"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.other.kanban.subtext",
                            defaultMessage:
                                "Visualize work and maximize efficiency with a kanban board"
                        })
                    }
                },
                {
                    option: {
                        ...templateChoices.otherProjectManagement,
                        title: formatMessage({
                            id: "TransparentTemplateV2.other.projectManagement.title",
                            defaultMessage: "Project management"
                        }),
                        subtext: formatMessage({
                            id: "TransparentTemplateV2.other.projectManagement.subtext",
                            defaultMessage:
                                "Manage, track, and assign tasks to streamline project workflows"
                        })
                    }
                }
            ];
        default:
            return defaultTemplateOptions;
    }
}
